import React, { useState, useEffect } from "react";
import "../../styles/Reports.scss";
import Modal from "react-modal";
import Select from "react-select";
import {
  getAllClients,
  getAllRecruiters,
  getDailyReport,
  getUserByReport,
  getUsersByRoleId,
} from "../../services/api";
import ExcelJS from "exceljs";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function UsersByReport() {
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Daily Report");

    // Add header row
    worksheet.columns = [
      { header: "S.NO", key: "sno" },
      { header: "Recruiter", key: "recruiter_name" },
      { header: "No.of Interviews", key: "no_of_interviews" },
      { header: "L1", key: "l1_count" },
      { header: "L2", key: "l2_count" },
      { header: "Client Discussion", key: "client_discussion" },
      
    ];

    // Add data rows
    reportData.forEach((item, index) => {
      worksheet.addRow({
        sno: index + 1,
        recruiter_name: item.recruiter_name || "-",
        no_of_interviews: item.no_of_interviews || "-",
        l1_count: item.l1_count || "-",
        l2_count: item.l2_count || "-",
        client_discussion: item.client_discussion || "-",
        
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "UserByReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [selectedRecruiter, startDate, endDate, reportData.length]);

  // Convert recruiters to react-select options
  const recruiterOptions = recruiters.map((recruiter) => ({
    value: recruiter.id,
    label: recruiter.name,
  }));

  // for final summary filter along with pagination
  const filteredData = reportData;

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

 

  // To get final summary data
  const fetchDailyReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getUserByReport(
        selectedRecruiter?.value || "",
        startDate,
        endDate
      );
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setReportData(res.result_info);
      } else {
        setError(res?.message || "Failed to fetch report data");
        setReportData([]);
      }
    } catch (err) {
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  

  // Fetch recruiters for role_id 4 (Recruiter)
  useEffect(() => {
    setLoading(true);
    const fetchRecruiters = async () => {
      setRecruiters([]);
      try {
        const res = await getAllRecruiters();
        if (res?.status === "S" && Array.isArray(res.result_info)) {
          setRecruiters(res.result_info);
        }
      } catch (error) {
        console.error("Error fetching recruiters:", error);
        setError("Failed to fetch recruiters");
      } finally {
        setLoading(false);
      }
    };
    fetchRecruiters();
  }, []);

  // Fetch report data when client or dates change
  useEffect(() => {
    fetchDailyReport();
  }, [selectedRecruiter, startDate, endDate]);

   function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }


  return (
    <div className="reports-page">
      <div className="reports-header-row">
        <div className="reports-title-group">
          <h1>User by Report</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="export-btn" onClick={handleExport}>
            Export
          </button>
        </div>
      </div>
      <div className="reports-table-controls">
        <div className="filters-row">
          <div className="select-wrapper">
              <Select
                placeholder="Select a Recruiter"
                value={selectedRecruiter}
                onChange={setSelectedRecruiter}
                options={recruiterOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 220,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
                isSearchable
              />
          </div>
          <div className="date-filters">
            <div className="date-input-wrapper">
              {!startDate && (
                <span className="placeholder-text">Start Date</span>
              )}
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  
                  if (endDate && newStartDate > endDate) {
                    // alert('Start date cannot be later than end date');
                    return;
                  }
                  
                  setStartDate(newStartDate);
                }}
                className="date-input"
              />
            </div>

            <div className="date-input-wrapper">
              {!endDate && <span className="placeholder-text">End Date</span>}
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  
                  if (startDate && newEndDate < startDate) {
                    // alert('End date cannot be earlier than start date');
                    return;
                  }
                  
                  setEndDate(newEndDate);
                }}
                className="date-input"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="reports-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading report data...
          </div>
        ) : (
          <table className="reports-table" data-columns="6">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Recruiter</th>
                <th>No. of Interviews</th>
                <th>L1</th>
                <th>L2</th>
                <th>Client Discussion</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No reports found!
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.correl_id || index}>
                    <td>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>{item.recruiter_name || "--"}</td>
                    <td>{item.no_of_interviews || 0}</td>
                    <td>{item.l1_count || 0}</td>
                    <td>{item.l2_count || 0}</td>
                    <td>{item.client_discussion || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
      {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
    </div>
  );
}

export default UsersByReport;
